import { Dispatch, SetStateAction, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UseMutationResult, useQueryClient } from '@tanstack/react-query';
import {
  useAddCounterIncrementsMutation,
  useRemoveCounterIncrementsMutation,
} from '@youshift/shared/hooks/mutations/chains';
import { SectionSlot, CustomCounter } from '@youshift/shared/types';

import Modal from '../../../../components/Modal';
import { SectionSlotsReducerActionType } from './reducers';
import { useItrContext } from '../../../../layouts/IterationRootLayout/IterationRootLayout';

interface BulkEditCounterIncrementsProps {
  isBulkEditCounterIncrementsOpen: boolean;
  setIsBulkEditCounterIncrementsOpen: (open: boolean) => void;
  selectedSectionSlots: Set<number>;
  setSelectedSectionSlots: (slots: Set<number>) => void;
  sectionSlots: Record<number, SectionSlot>;
  counters: CustomCounter[];
  selectedCounterId: number | undefined;
  setSelectedCounterId: Dispatch<SetStateAction<number | undefined>>;
  incrementValue: number;
  setIncrementValue: Dispatch<SetStateAction<number>>;
  handleSectionSlotsDispatch: (action: SectionSlotsReducerActionType) => void;
}

export default function BulkEditCounterIncrements({
  isBulkEditCounterIncrementsOpen,
  setIsBulkEditCounterIncrementsOpen,
  selectedSectionSlots,
  setSelectedSectionSlots,
  sectionSlots,
  counters,
  selectedCounterId,
  setSelectedCounterId,
  incrementValue,
  setIncrementValue,
  handleSectionSlotsDispatch,
}: BulkEditCounterIncrementsProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { iteration } = useItrContext();

  const [mode, setMode] = useState<'add' | 'remove'>('add');

  const addCounterIncrementsMutation = useAddCounterIncrementsMutation(
    queryClient,
    {
      onSuccess: () => {
        setIsBulkEditCounterIncrementsOpen(false);
        handleSectionSlotsDispatch({
          type: 'ADD_COUNTER_INCREMENT',
          counterId: selectedCounterId!,
          value: incrementValue,
          idSectionSlots: Array.from(selectedSectionSlots),
        });
        setSelectedSectionSlots(new Set());
        queryClient.invalidateQueries({
          queryKey: ['sectionSlots', String(iteration.id_itr)],
        });
      },
    },
  );

  const removeCounterIncrementsMutation = useRemoveCounterIncrementsMutation(
    queryClient,
    {
      onSuccess: () => {
        setIsBulkEditCounterIncrementsOpen(false);
        handleSectionSlotsDispatch({
          type: 'REMOVE_COUNTER_INCREMENT',
          counterId: selectedCounterId!,
          idSectionSlots: Array.from(selectedSectionSlots),
        });
        setSelectedSectionSlots(new Set());
        queryClient.invalidateQueries({
          queryKey: ['sectionSlots', String(iteration.id_itr)],
        });
      },
    },
  );

  const handleBulkEditCounterIncrements = () => {
    if (!selectedCounterId) {
      return;
    }

    if (mode === 'add') {
      addCounterIncrementsMutation.mutate({
        id_custom_counter: selectedCounterId,
        section_slots: Array.from(selectedSectionSlots),
        value: incrementValue,
        id_chain: iteration.id_chain,
      });
    } else {
      removeCounterIncrementsMutation.mutate({
        id_custom_counter: selectedCounterId,
        section_slots: Array.from(selectedSectionSlots),
        id_chain: iteration.id_chain,
      });
    }
  };

  useEffect(() => {
    setSelectedCounterId(undefined);
  }, [mode, setSelectedCounterId]);

  return (
    <Modal
      isOpen={isBulkEditCounterIncrementsOpen}
      onClose={() => setIsBulkEditCounterIncrementsOpen(false)}
      size="md"
      editButtons
      handleSave={handleBulkEditCounterIncrements}
      loadingSave={
        addCounterIncrementsMutation.isPending ||
        removeCounterIncrementsMutation.isPending
      }
      disableSave={!selectedCounterId || (mode === 'add' && !incrementValue)}
    >
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">
          {t('manager.sectionsConfig.bulkEditCounters')}
        </h2>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('manager.sectionsConfig.selectActionCounter')}
          </label>
          <div className="space-y-2 mb-4">
            <div className="flex items-center">
              <input
                id="add-mode"
                name="action-mode"
                type="radio"
                checked={mode === 'add'}
                onChange={() => setMode('add')}
                className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label
                htmlFor="add-mode"
                className="ml-3 block text-sm font-medium text-gray-700"
              >
                {t('manager.sectionsConfig.addCounterIncrement')}
              </label>
            </div>
            <div className="flex items-center">
              <input
                id="remove-mode"
                name="action-mode"
                type="radio"
                checked={mode === 'remove'}
                onChange={() => setMode('remove')}
                className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label
                htmlFor="remove-mode"
                className="ml-3 block text-sm font-medium text-gray-700"
              >
                {t('manager.sectionsConfig.removeCounterIncrement')}
              </label>
            </div>
          </div>
        </div>

        <div>
          <label htmlFor="counter" className="block text-sm text-gray-700">
            {mode === 'add'
              ? t('manager.sectionsConfig.selectCounterToAdd')
              : t('manager.sectionsConfig.selectCounterToRemove')}
          </label>
          <select
            id="counter"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            onChange={e => setSelectedCounterId(Number(e.target.value))}
            value={selectedCounterId || ''}
          >
            <option value="">
              {t('manager.sectionsConfig.selectCounter')}
            </option>
            {counters.map(counter => (
              <option
                key={counter.id_custom_counter}
                value={counter.id_custom_counter}
              >
                {counter.name}
              </option>
            ))}
          </select>
        </div>

        {mode === 'add' && (
          <div>
            <label htmlFor="value" className="block text-sm text-gray-700">
              {t('manager.sectionsConfig.incrementValue')}
            </label>
            <input
              type="number"
              id="value"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              value={incrementValue}
              onChange={e => setIncrementValue(parseFloat(e.target.value))}
              step="0.1"
            />
          </div>
        )}

        {selectedCounterId !== undefined &&
          !Number.isNaN(selectedCounterId) &&
          counters.some(
            counter => counter.id_custom_counter === selectedCounterId,
          ) && (
            <div className="mt-4 p-3 bg-gray-50 rounded-md">
              <p className="text-sm text-gray-700 mb-2">
                {mode === 'add'
                  ? t('manager.sectionsConfig.willAddCounterIncrement')
                  : t('manager.sectionsConfig.willRemoveCounterIncrement')}
              </p>
              <div className="flex items-center gap-2">
                {counters
                  .filter(
                    counter => counter.id_custom_counter === selectedCounterId,
                  )
                  .map(counter => (
                    <div
                      key={counter.id_custom_counter}
                      className="inline-flex items-center gap-x-1.5 rounded-full px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200 bg-blue-100"
                    >
                      {counter.name}
                    </div>
                  ))}
                <span className="text-sm text-gray-700">
                  {t('manager.sectionsConfig.toSelectedSlots', {
                    count: selectedSectionSlots.size,
                  })}
                </span>
                {mode === 'add' && (
                  <span className="text-sm font-medium text-gray-900">
                    {t('manager.sectionsConfig.withValue', {
                      value: incrementValue,
                    })}
                  </span>
                )}
              </div>
            </div>
          )}
      </div>
    </Modal>
  );
}
